import { AUTH_ERROR_CODES, type AuthErrorCode } from "./auth-error-codes";

interface ErrorMessageConfig {
    title: string;
    description: string;
    suggestion?: string;
    isRetryable: boolean;
}

/**
 * User-friendly error messages mapped from error codes
 */
export const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, ErrorMessageConfig> = {
    [AUTH_ERROR_CODES.INVALID_TOKEN]: {
        title: "Session Invalid",
        description: "Your session could not be verified.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.TOKEN_EXPIRED]: {
        title: "Session Expired",
        description: "Your session has expired due to inactivity.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.TOKEN_REVOKED]: {
        title: "Session Ended",
        description: "Your session was ended, possibly from another device.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.TOKEN_ROTATED]: {
        title: "Session Updated",
        description: "Your session was refreshed. Please try again.",
        suggestion: "If this persists, please sign in again.",
        isRetryable: true,
    },
    [AUTH_ERROR_CODES.USER_NOT_FOUND]: {
        title: "Account Not Found",
        description: "We couldn't find an account associated with your login.",
        suggestion: "Please contact your administrator to set up your account.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.USER_INACTIVE]: {
        title: "Account Deactivated",
        description: "Your account has been deactivated.",
        suggestion: "Please contact your administrator for assistance.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.TENANT_ACCESS_DENIED]: {
        title: "Access Denied",
        description: "You don't have access to this organization.",
        suggestion:
            "Please contact your administrator if you believe this is an error.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.PERMISSION_DENIED]: {
        title: "Permission Denied",
        description: "You don't have permission to perform this action.",
        suggestion: "Please contact your administrator to request access.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.RATE_LIMITED]: {
        title: "Too Many Requests",
        description: "You've made too many requests. Please wait a moment.",
        isRetryable: true,
    },
    [AUTH_ERROR_CODES.INVALID_REQUEST]: {
        title: "Invalid Request",
        description: "The request could not be processed.",
        suggestion: "Contact support if this persists.",
        isRetryable: true,
    },
    [AUTH_ERROR_CODES.INTERNAL_ERROR]: {
        title: "Something Went Wrong",
        description: "We encountered an unexpected error.",
        suggestion: "Contact support if this persists.",
        isRetryable: true,
    },
    [AUTH_ERROR_CODES.NETWORK_ERROR]: {
        title: "Connection Lost",
        description:
            "We couldn't reach the server. Check your internet connection.",
        isRetryable: true,
    },
    [AUTH_ERROR_CODES.EXCHANGE_FAILED]: {
        title: "Authentication Failed",
        description: "We couldn't complete the sign-in process.",
        isRetryable: true,
    },
    [AUTH_ERROR_CODES.NOT_AUTHENTICATED]: {
        title: "Not Signed In",
        description: "You need to sign in to access this page.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.SESSION_EXPIRED]: {
        title: "Session Expired",
        description: "Your session has expired.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.TOKEN_REFRESH_FAILED]: {
        title: "Session Refresh Failed",
        description: "We couldn't refresh your session.",
        isRetryable: false,
    },
    [AUTH_ERROR_CODES.UNKNOWN_ERROR]: {
        title: "Something Went Wrong",
        description: "An unexpected error occurred.",
        suggestion: "Contact support if this persists.",
        isRetryable: true,
    },
};

/**
 * Get user-friendly error message config for an error code
 */
export function getErrorMessage(code: AuthErrorCode): ErrorMessageConfig {
    return (
        AUTH_ERROR_MESSAGES[code] ??
        AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.UNKNOWN_ERROR]
    );
}
